<mat-card class="p-0">
  <app-single-crawler-list-toolbar
    [count]="list.paginator.totalDocs"
    labelKey="INTEGRATIONS.CRAWL_URLS.TITLE"
    countLabelKeySingular="INTEGRATIONS.CRAWL_URLS.URL"
    countLabelKeyPlural="INTEGRATIONS.CRAWL_URLS.URLS"
    [query]="list.filters.query"
    (queryChange)="onQueryChange($event)">

    <div filters class="fx-row align-items-center">
      @if (executionOptions.length > 1) {
        <app-select [emitOnInit]="false" (change)="onExecutionChange($event)" class="mr-10"
                    [value]="list.filters.executionId"
                    [label]="'INTEGRATIONS.CRAWL_URLS.EXECUTION' | translate"
                    [options]="executionOptions"></app-select>
      }
      <app-select [emitOnInit]="false" (change)="onStatusChange($event)" class="mr-10"
                  [label]="'INTEGRATIONS.CRAWL_URLS.STATUS' | translate"
                  [options]="statusSelectOptions"></app-select>
    </div>

    <span primaryAction class="fx-row align-items-center">
      <span [matTooltip]="canLaunch() ? '' : ('ERRORS.' + (eligibility?.crawlBlockReason ?? 'GENERIC')) | translate"
            [matTooltipDisabled]="canLaunch()"
            [matTooltipClass]="{'uzai-tooltip':true}">
        <button class="main-button" [class.disabled]="!canLaunch()" [disabled]="!canLaunch()" (click)="launchCrawl()">
          <span class="fx-row gap-5 align-items-center">
            <mat-icon fontSet="material-icons-outlined" [class.spin]="isCrawlingAll()"> {{
                isCrawlingAll()
                  ? 'refresh'
                  : 'travel_explore'
              }}</mat-icon>
            {{ 'ACTIONS.CRAWL' | translate }}
            @if (crawler().type === CrawlerType.DISCOVERY && canLaunch() && eligibility?.capturableCount !== undefined) {
              ({{ eligibility?.capturableCount }})
            }
          </span>
        </button>
      </span>
    </span>

    <div secondaryAction class="fx-row align-items-center gap-10">
      <!-- kept while the retry runs, spinning, so the buttons do not move under the cursor -->
      @if (eligibility?.canRetryFailedUrls || isRetryingFailed()) {
        <button class="button" (click)="launchCrawl(true)"
                [class.disabled]="isRetryingFailed()" [disabled]="isRetryingFailed()">
          <span class="fx-row align-items-center gap-5">
            <mat-icon fontSet="material-icons-outlined" [class.spin]="isRetryingFailed()">refresh</mat-icon>
            {{ 'ACTIONS.CRAWL_AGAIN_FAILED_URLS' | translate }}
          </span>
        </button>
      }
      @if (canCancelCrawl) {
        <!-- Greyed until the run is known to the server: before that there is nothing to cancel yet. -->
        <button class="danger-button" (click)="cancelCrawl()"
                [disabled]="!eligibility?.runningExecutionId" [class.disabled]="!eligibility?.runningExecutionId"
                matTooltip="{{ (eligibility?.runningExecutionId ? 'INTEGRATIONS.CRAWL_URLS.CANCEL_TOOLTIP' : 'INTEGRATIONS.CRAWL_URLS.CANCEL_STARTING') | translate }}"
                [matTooltipClass]="{'uzai-tooltip':true}">
          <span class="fx-row align-items-center gap-5">
            <mat-icon fontSet="material-icons-outlined">close</mat-icon>
            {{ 'ACTIONS.CANCEL' | translate }}
          </span>
        </button>
      }
    </div>
  </app-single-crawler-list-toolbar>

  <mat-divider></mat-divider>

  @if (!list.loading && list.paginator.totalDocs === 0) {
    @if ((crawler().type === CrawlerType.DISCOVERY && eligibility?.isDiscoveryRunning) || eligibility?.canRunCrawl) {
      <app-empty-placeholder
        [offset]="200"
        [title]="'INTEGRATIONS.CRAWL_URLS.NO_CRAWL_TITLE'"
        [message]="'INTEGRATIONS.CRAWL_URLS.NO_CRAWL_MESSAGE'">
      </app-empty-placeholder>
    } @else {
      <div class="p-16">
        <span class="text light">{{'DOCUMENTS_LIST.NO_DOCUMENTS' | translate}}</span>
      </div>
    }
  } @else {
    <table mat-table [dataSource]="list.paginator.docs" style="z-index: 0;" matSort
           (matSortChange)="onSortChange($event)">

      <ng-container matColumnDef="url">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          <span class="small light bold">{{ 'INTEGRATIONS.CRAWL_URLS.URL' | translate }}</span>
        </th>
        <td mat-cell *matCellDef="let crawlUrl">
          <a target="_blank" [href]="crawlUrl.url" class="text bold">{{ crawlUrl.url }}</a>
        </td>
      </ng-container>

      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          <span class="small light bold">{{ 'INTEGRATIONS.CRAWL_URLS.STATUS' | translate }}</span>
        </th>
        <td mat-cell *matCellDef="let crawlUrl">
          <div class="fx-row align-items-center justify-content-start">
          <span [class.green]="crawlUrl.status === 'success'"
                [class.red]="crawlUrl.status === 'failed'"
                class="chip light">
            {{ 'INTEGRATIONS.CRAWL_URLS.STATUSES.' + crawlUrl.status | translate }}
          </span>
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="document">
        <th mat-header-cell *matHeaderCellDef>
          <span class="small light bold">{{ 'INTEGRATIONS.CRAWL_URLS.DOCUMENT' | translate }}</span>
        </th>
        <td mat-cell *matCellDef="let crawlUrl">
          @if (documentRef(crawlUrl); as doc) {
            <a class="text bold clickable" (click)="openDocument(crawlUrl)">
              <mat-icon class="dark mr-5" style="transform:scale(0.8); vertical-align:middle;"
                        fontSet="material-icons-outlined">description</mat-icon>{{ doc.name }}
            </a>
          } @else {
            <span class="text light">-</span>
          }
        </td>
      </ng-container>

      <ng-container matColumnDef="error">
        <th mat-header-cell *matHeaderCellDef>
          <span class="small light bold">{{ 'INTEGRATIONS.CRAWL_URLS.ERROR' | translate }}</span>
        </th>
        <td mat-cell *matCellDef="let crawlUrl">
          @if (crawlUrl.status === 'failed' && crawlUrl.error) {
            <span class="text red ellipsis" [matTooltip]="canReadRawErrors() ? crawlUrl.error : ''"
                  [matTooltipDisabled]="!canReadRawErrors()"
                  [matTooltipClass]="{'uzai-tooltip':true}">{{ failureReason(crawlUrl) | translate }}</span>
          } @else {
            <span class="text light">-</span>
          }
        </td>
      </ng-container>

      <ng-container matColumnDef="processedAt">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          <span class="small light bold">{{ 'INTEGRATIONS.CRAWL_URLS.PROCESSED_AT' | translate }}</span>
        </th>
        <td mat-cell *matCellDef="let crawlUrl">
          <time [dateTime]="crawlUrl.processedAt" class="text light">{{ date(crawlUrl.processedAt) }}</time>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let crawlUrl; columns: displayedColumns;"></tr>
    </table>
    <mat-paginator [length]="list.paginator.totalDocs || 0" [pageSize]="list.paginator.limit || 0"
                   [pageIndex]="list.paginator.page ? (list.paginator.page - 1) : 0"
                   [pageSizeOptions]="[10, 25, 50, 100]"
                   (page)="handlePage($event)">
    </mat-paginator>
  }

  @if (notCaptured && notCaptured.totalDocs > 0) {
    <!-- reminder, read-only: the pages are chosen in the discovery step -->
    <mat-divider></mat-divider>
    <div class="p-16 fx-column gap-10">
      <span class="text bold">{{ 'INTEGRATIONS.CRAWL_URLS.NOT_CAPTURED_TITLE' | translate: { count: notCaptured.totalDocs } }}</span>
      <span class="small light">{{ 'INTEGRATIONS.CRAWL_URLS.NOT_CAPTURED_HINT' | translate }}</span>
      @for (page of notCaptured.docs; track page._id) {
        <div class="fx-row align-items-center gap-10">
          <span class="chip light">{{ 'INTEGRATIONS.BACKLINK_CRAWLER.EXCLUDED' | translate }}</span>
          <a target="_blank" [href]="page.url" class="text light">{{ page.url }}</a>
        </div>
      }
      @if (notCaptured.totalDocs > notCaptured.docs.length) {
        <span class="small light">{{ 'INTEGRATIONS.CRAWL_URLS.NOT_CAPTURED_MORE' | translate: { count: notCaptured.totalDocs - notCaptured.docs.length } }}</span>
      }
    </div>
  }

</mat-card>
